import Link from '@/routing/AppLink';

import { IconArrowRightRound } from '@/components/apps/ui/icon';
import {
  buildInquiryHref,
  industrialCaseCtaSource,
} from '@/components/apps/ui/inquiryForm/inquiryCtaSource';

type Props = {
  caseId: string;
  title: string;
  description: string;
  inquiryLabel: string;
  backLabel: string;
  isInteractive?: boolean;
  /** Reports the inquiry click. Absent in the admin preview. */
  onInquiry?: () => void;
};

/**
 * ### IndustrialCaseClosingCta
 *
 * Closes a case with the inquiry and return actions shown on the public page.
 * Preview mode renders the same affordances as non-interactive content.
 *
 * @param props Case identity, labels, and interaction mode
 * @returns The closing call-to-action section
 */
export function IndustrialCaseClosingCta({
  caseId,
  title,
  description,
  inquiryLabel,
  backLabel,
  isInteractive = true,
  onInquiry,
}: Props) {
  const inquiryContent = (
    <>
      <span className="text-lg">{inquiryLabel}</span>
      <IconArrowRightRound
        className="transition-transform duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none"
        size={20}
        aria-hidden="true"
      />
    </>
  );
  const backContent = (
    <>
      <span>{backLabel}</span>
      <IconArrowRightRound
        className="transition-transform duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 motion-reduce:transform-none motion-reduce:transition-none"
        size={16}
        aria-hidden="true"
      />
    </>
  );

  return (
    <section className="relative z-1 grid border-t border-[#d8d8d4] md:grid-cols-4">
      <div className="px-5 py-10 md:col-span-3 md:col-start-2 md:px-10 md:py-14 lg:px-20">
        <div className="max-w-180">
          <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_220px] md:items-end md:gap-12">
            <div>
              <h2 className="mt-4 text-[clamp(28px,3.4vw,40px)] font-semibold leading-[1.16] tracking-[-0.03em] text-apps-brandGrayBlack text-balance">
                {title}
              </h2>
              <p className="mt-5 max-w-125 text-[15px] font-medium leading-[1.7] tracking-[-0.01em] text-[#4f4f4b] md:text-base">
                {description}
              </p>
            </div>

            {isInteractive ? (
              <Link
                className="group my-auto self-center inline-flex min-h-14 w-full items-center justify-between bg-apps-brandGrayBlack px-5 text-sm font-semibold text-apps-coreGrayWhite transition-colors duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] hover:bg-apps-corePrimary01 active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none"
                href={buildInquiryHref({
                  interest: 'poc-terms',
                  source: industrialCaseCtaSource(caseId),
                })}
                prefetch={false}
                onClick={onInquiry}
                data-analytics-content-id="industrial_case_inquiry"
                data-analytics-content-type="case_cta"
              >
                {inquiryContent}
              </Link>
            ) : (
              <span className="group my-auto self-center inline-flex min-h-14 w-full items-center justify-between bg-apps-brandGrayBlack px-5 text-sm font-semibold text-apps-coreGrayWhite">
                {inquiryContent}
              </span>
            )}
          </div>

          <div className="mt-9 border-t border-[#d2d2cd] pt-5">
            {isInteractive ? (
              <Link
                className="group inline-flex min-h-11 items-center gap-2 py-2 text-sm font-semibold text-[#545450] transition-colors duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-apps-corePrimary01 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01 motion-reduce:transition-none"
                href="/solution/industrial"
                prefetch={false}
              >
                {backContent}
              </Link>
            ) : (
              <span className="group inline-flex min-h-11 items-center gap-2 py-2 text-sm font-semibold text-[#545450]">
                {backContent}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
